import { inject } from '@angular/core'
import { DOCUMENT } from '@angular/common'
import type { HttpInterceptorFn } from '@angular/common/http'
import { environment } from '../../../environments/environment'

const XSRF_COOKIE = 'XSRF-TOKEN'
const XSRF_HEADER = 'X-XSRF-TOKEN'

/** Reads a cookie by name; returns the decoded value or null when absent. */
function readCookie(cookies: string, name: string): string | null {
  for (const part of cookies.split(';')) {
    const eq = part.indexOf('=')
    if (eq === -1) continue
    if (part.slice(0, eq).trim() !== name) continue
    return decodeURIComponent(part.slice(eq + 1).trim())
  }
  return null
}

/**
 * Attaches X-XSRF-TOKEN to mutating API requests.
 *
 * Angular's built-in XSRF interceptor (withXsrfConfiguration) deliberately
 * skips ABSOLUTE urls — and the dev apiBase is http://localhost:3333/api
 * while the page origin is :4200, so every cross-origin mutation reached the
 * API without the header and got 403 "Invalid CSRF token" (login only worked
 * because that route is CSRF-exempt server-side). This interceptor closes the
 * gap: non-GET/HEAD requests to the API base get the header from the
 * XSRF-TOKEN cookie when the built-in one hasn't already set it.
 */
export const xsrfInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.method === 'GET' || req.method === 'HEAD') return next(req)
  if (!req.url.startsWith(environment.apiBase)) return next(req)
  if (req.headers.has(XSRF_HEADER)) return next(req)
  const token = readCookie(inject(DOCUMENT).cookie ?? '', XSRF_COOKIE)
  if (token === null) return next(req)
  return next(req.clone({ headers: req.headers.set(XSRF_HEADER, token) }))
}
